'use client';

import React from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';

/* ─── Gallery images ─────────────────────────────────── */
const CREATIVES = [
    { src: '/images/our-work/instagram/lw-post-01.webp',   client: '@lead_workz',              label: 'Brand Identity' },
    { src: '/images/our-work/instagram/lw-post-02.webp',   client: '@lead_workz',              label: 'Team Culture' },
    { src: '/images/our-work/instagram/koza-post-05.webp', client: '@koza.aesthetic.care',     label: 'Social Campaign' },
    { src: '/images/our-work/instagram/d1s-insta-05.webp', client: '@design1studio_interiors', label: 'Content Strategy' },
    { src: '/images/our-work/instagram/lw-post-10.webp',   client: '@lead_workz',              label: 'Performance Ad' },
    { src: '/images/our-work/instagram/koza-post-07.webp', client: '@koza.aesthetic.care',     label: 'Awareness Campaign' },
    { src: '/images/our-work/instagram/d1s-insta-01.webp', client: '@design1studio_interiors', label: 'Visual Storytelling' },
    { src: '/images/our-work/instagram/lw-post-03.webp',   client: '@lead_workz',              label: 'Engagement Post' },
    { src: '/images/our-work/instagram/koza-post-06.webp', client: '@koza.aesthetic.care',     label: 'Brand Recall' },
    { src: '/images/our-work/instagram/d1s-insta-03.webp', client: '@design1studio_interiors', label: 'Lead Creative' },
    { src: '/images/our-work/instagram/lw-post-11.webp',   client: '@lead_workz',              label: 'Conversion Creative' },
];

/* Double for seamless infinite loop */
const DOUBLED = [...CREATIVES, ...CREATIVES];

/* rotateY tilt — inward-facing gallery fan */
function getTilt(index: number, total: number): number {
    const center = (total - 1) / 2;
    return -((index - center) / Math.max(center, 1)) * 28;
}

/* ─── Single Poster ──────────────────────────────────── */
function Poster({ src, client, label, tiltY, floatDelay }: {
    src: string; client: string; label: string; tiltY: number; floatDelay: number;
}) {
    return (
        <motion.div
            className="shrink-0"
            style={{ perspective: 1500 }}
            whileHover="hover"
            initial="rest"
            animate="rest"
        >
            {/* Floating lift */}
            <motion.div
                animate={{ y: [0, -12, 0] }}
                transition={{ duration: 3.2 + floatDelay * 0.4, ease: 'easeInOut', repeat: Infinity, delay: floatDelay }}
            >
                {/* Y-axis tilt */}
                <motion.div
                    variants={{
                        rest:  { rotateY: tiltY,       scale: 0.93, filter: 'brightness(0.85)' },
                        hover: { rotateY: tiltY * 0.1, scale: 1.06, filter: 'brightness(1.04)' },
                    }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    style={{ transformStyle: 'preserve-3d' }}
                >
                    {/* Black frame */}
                    <motion.div
                        variants={{
                            rest:  { boxShadow: '0 16px 48px rgba(0,0,0,0.18), 0 4px 10px rgba(0,0,0,0.10)' },
                            hover: { boxShadow: '0 36px 90px rgba(0,0,0,0.32), 0 8px 20px rgba(0,0,0,0.16)' },
                        }}
                        transition={{ duration: 0.4 }}
                        style={{
                            width: 290,
                            background: '#111111',
                            padding: 5,
                            borderRadius: 8,
                            border: '1px solid rgba(255,255,255,0.06)',
                            position: 'relative',
                        }}
                    >
                        {/* Cream mat board */}
                        <div style={{ background: '#f0ebe3', padding: '10px 10px 32px', borderRadius: 3 }}>
                            {/* Image */}
                            <div style={{ position: 'relative', width: '100%', aspectRatio: '1/1', overflow: 'hidden', borderRadius: 2 }}>
                                <Image src={src} alt={label} fill className="object-cover" sizes="270px" draggable={false} />
                            </div>
                            {/* Mat label */}
                            <div style={{ paddingTop: 9 }}>
                                <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 9, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(0,0,0,0.32)', margin: 0 }}>
                                    {client}
                                </p>
                                <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 13, fontWeight: 600, color: 'rgba(0,0,0,0.72)', margin: '3px 0 0', letterSpacing: '0.01em' }}>
                                    {label}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>
        </motion.div>
    );
}

/* ─── Main export ────────────────────────────────────── */
export default function CreativeShowcase() {
    return (
        <section style={{ background: '#f5f4f0', padding: '96px 0 80px', overflow: 'hidden' }}>

            {/* CSS keyframes for infinite scroll + edge fade */}
            <style>{`
                @keyframes gallery-scroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                .gallery-track {
                    display: flex;
                    align-items: center;
                    gap: 28px;
                    width: max-content;
                    animation: gallery-scroll 38s linear infinite;
                    padding: 40px 0 20px;
                }
                .gallery-track:hover {
                    animation-play-state: paused;
                }
            `}</style>

            {/* ── Heading ── */}
            <div style={{ textAlign: 'center', marginBottom: 64, padding: '0 24px' }}>
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    style={{
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.28em',
                        textTransform: 'uppercase',
                        color: 'rgba(0,0,0,0.32)',
                        marginBottom: 14,
                    }}
                >
                    Creative Portfolio
                </motion.p>
                <motion.h2
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.08 }}
                    style={{
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                        fontWeight: 800,
                        fontSize: 'clamp(34px, 4.8vw, 64px)',
                        lineHeight: 1.0,
                        letterSpacing: '-0.025em',
                        color: '#0a0a0a',
                        margin: 0,
                    }}
                >
                    Creativity That{' '}
                    <span style={{ color: '#3730a3' }}>Converts</span>
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.14 }}
                    style={{
                        fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                        fontSize: 15,
                        color: 'rgba(0,0,0,0.42)',
                        marginTop: 18,
                        lineHeight: 1.6,
                    }}
                >
                    From bold visuals to smart storytelling, here&apos;s the work that helped our
                    <br />clients capture attention and conversions.
                </motion.p>
            </div>

            {/* ── Gallery ── */}
            <div
                style={{
                    perspective: '1500px',
                    perspectiveOrigin: '50% 50%',
                    /* Edge fade */
                    maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
                    WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
                }}
            >
                <div className="gallery-track">
                    {DOUBLED.map((item, i) => (
                        <Poster
                            key={i}
                            src={item.src}
                            client={item.client}
                            label={item.label}
                            tiltY={getTilt(i % CREATIVES.length, CREATIVES.length)}
                            floatDelay={(i % CREATIVES.length) * 0.22}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
